import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Printer,
  Copy,
  CheckCircle2,
  Clock,
  Package,
  Truck,
  XCircle,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { BRAND_NAME } from '../../lib/brand';
import { formatCurrency } from '../../lib/currency';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale/tr';
import {
  getOrderById,
  updateOrderStatus as updateOrderStatusService,
  sendOrderEmail,
} from '../../services/admin/orders';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const statusLabels = {
  pending: 'Ödeme Bekleniyor',
  paid: 'Ödeme Alındı',
  shipped: 'Kargoya Verildi',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusIcons = {
  pending: Clock,
  paid: CheckCircle2,
  shipped: Package,
  delivered: Truck,
  cancelled: XCircle,
};

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [emailSubject, setEmailSubject] = useState(`${BRAND_NAME} Bilgilendirme`);
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchOrder = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const orderData = await getOrderById(orderId);
      setOrder(orderData);
      if (!silent) {
        setNewStatus(orderData.status);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: 'Sipariş yüklenirken bir hata oluştu.',
          variant: 'destructive',
        });
        navigate('/admin/orders');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [orderId, navigate, toast]);

  useAdminAutoRefresh(fetchOrder, { interval: 5000, deps: [orderId] });

  const handleStatusUpdate = async () => {
    if (!order || newStatus === order.status) return;

    setUpdatingStatus(true);
    try {
      const statusHistoryEntry = {
        status: newStatus,
        note: statusNote || undefined,
      };

      const updated = await updateOrderStatusService(order.id, newStatus, statusHistoryEntry);
      setOrder(updated);

      toast({
        title: 'Başarılı',
        description: 'Sipariş durumu güncellendi.',
      });

      setStatusNote('');
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: 'Hata',
        description: 'Durum güncellenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailBody.trim()) {
      toast({
        title: 'Hata',
        description: 'E-posta içeriği boş olamaz.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setSendingEmail(true);
      await sendOrderEmail(order.id, {
        subject: emailSubject,
        body: emailBody,
      });
      toast({
        title: 'Başarılı',
        description: 'E-posta gönderim kuyruğuna alındı.',
      });
      setEmailBody('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Hata',
        description: 'E-posta gönderilirken hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const copyOrderNumber = () => {
    if (order?.orderNumber) {
      navigator.clipboard.writeText(order.orderNumber);
      toast({
        title: 'Kopyalandı',
        description: 'Sipariş numarası kopyalandı.',
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Sipariş bulunamadı</p>
          <Button onClick={() => navigate('/admin/orders')} className="mt-4">
            Siparişlere Dön
          </Button>
        </div>
      </AdminLayout>
    );
  }

  // Safely convert timestamp-like values to Date
  let orderDate;
  if (order.createdAt) {
    if (order.createdAt.toDate && typeof order.createdAt.toDate === 'function') {
      orderDate = order.createdAt.toDate();
    } else if (order.createdAt.seconds) {
      // Timestamp object
      orderDate = new Date(order.createdAt.seconds * 1000);
    } else {
      orderDate = new Date(order.createdAt);
    }
  }
  
  // Fallback to current date if invalid
  if (!orderDate || isNaN(orderDate.getTime())) {
    orderDate = new Date();
  }

  const StatusIcon = statusIcons[order.status] || Clock;

  return (
    <AdminLayout>
      <div className="space-y-6 print:p-0">
        {/* Header */}
        <div className="admin-order-detail-header flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 print:hidden">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/orders')}>
              <ArrowLeft size={18} className="mr-2" />
              Geri
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Sipariş Detayı</h1>
              <p className="text-gray-600 mt-1">Sipariş #{order.orderNumber || order.id}</p>
            </div>
          </div>
          <div className="admin-toolbar">
            <Button variant="outline" onClick={copyOrderNumber}>
              <Copy size={18} className="mr-2" />
              Kopyala
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer size={18} className="mr-2" />
              Yazdır
            </Button>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-lg border p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${statusColors[order.status]?.split(' ')[0] || 'bg-gray-100'}`}>
                <StatusIcon className={`${statusColors[order.status]?.split(' ')[1] || 'text-gray-800'}`} size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Sipariş Durumu</h2>
                <Badge className={`mt-2 ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                  {statusLabels[order.status] || order.status}
                </Badge>
              </div>
            </div>

            {/* Status Update */}
            <div className="flex-1 max-w-md space-y-2">
              <Label>Durum Güncelle</Label>
              <div className="admin-toolbar admin-toolbar-row flex gap-2">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleStatusUpdate}
                  disabled={updatingStatus || newStatus === order.status}
                >
                  {updatingStatus ? 'Güncelleniyor...' : 'Güncelle'}
                </Button>
              </div>
              <Textarea
                placeholder="Durum değişikliği notu (opsiyonel)"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Manual Email */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold mb-4">Müşteriye E-posta Gönder</h2>
          <div className="space-y-3 max-w-2xl">
            <div>
              <Label htmlFor="emailSubject">Konu</Label>
              <Input
                id="emailSubject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="emailBody">Mesaj</Label>
              <Textarea
                id="emailBody"
                rows={4}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Müşteriye gönderilecek mesaj"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSendEmail} disabled={sendingEmail}>
                {sendingEmail ? 'Gönderiliyor...' : 'E-posta Gönder'}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-semibold mb-4">Müşteri Bilgileri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Ad Soyad</p>
                  <p className="font-medium">{order.customerInfo?.fullName || 'Bilinmiyor'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">E-posta</p>
                  <p className="font-medium">{order.customerInfo?.email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefon</p>
                  <p className="font-medium">{order.customerInfo?.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sipariş Tarihi</p>
                  <p className="font-medium">
                    {format(orderDate, 'dd MMMM yyyy, HH:mm', { locale: tr })}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Adres</p>
                  <p className="font-medium">
                    {order.customerInfo?.address || '-'}
                    <br />
                    {order.customerInfo?.district && order.customerInfo?.city
                      ? `${order.customerInfo.district} / ${order.customerInfo.city}`
                      : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-semibold mb-4">Sipariş Ürünleri</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beden/Renk</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Adet</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Birim Fiyat</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Toplam</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {order.items?.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-16 h-16 object-cover rounded"
                              />
                            )}
                            <div>
                              <p className="font-medium">{item.name}</p>
                              {item.productId && (
                                <p className="text-sm text-gray-500">Ürün ID: {item.productId}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {item.size && `Beden: ${item.size}`}
                          {item.size && item.color && ' / '}
                          {item.color && `Renk: ${item.color}`}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-medium">{item.quantity}</td>
                        <td className="px-4 py-4 text-right text-sm text-gray-500">
                          {formatCurrency(item.price || 0)}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-medium">
                          {formatCurrency((item.price || 0) * (item.quantity || 1))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Status Timeline */}
            {order.statusHistory && order.statusHistory.length > 0 && (
              <div className="bg-white rounded-lg border p-6">
                <h2 className="text-xl font-semibold mb-4">Durum Geçmişi</h2>
                <div className="space-y-4">
                  {order.statusHistory
                    .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                    .map((entry, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${statusColors[entry.status]?.split(' ')[0] || 'bg-gray-200'}`} />
                          {index < order.statusHistory.length - 1 && (
                            <div className="w-0.5 h-12 bg-gray-200" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{statusLabels[entry.status] || entry.status}</p>
                              <p className="text-sm text-gray-500">
                                {(() => {
                                  let changedDate;
                                  if (entry.changedAt) {
                                    if (entry.changedAt.toDate && typeof entry.changedAt.toDate === 'function') {
                                      changedDate = entry.changedAt.toDate();
                                    } else if (entry.changedAt.seconds) {
                                      changedDate = new Date(entry.changedAt.seconds * 1000);
                                    } else {
                                      changedDate = new Date(entry.changedAt);
                                    }
                                  }
                                  if (!changedDate || isNaN(changedDate.getTime())) {
                                    changedDate = new Date();
                                  }
                                  return format(changedDate, 'dd MMMM yyyy, HH:mm', { locale: tr });
                                })()}
                              </p>
                              {entry.changedBy && (
                                <p className="text-sm text-gray-500">Tarafından: {entry.changedBy}</p>
                              )}
                              {entry.note && (
                                <p className="text-sm text-gray-600 mt-1 italic">Not: {entry.note}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-semibold mb-4">Sipariş Özeti</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Ara Toplam</span>
                  <span className="font-medium">{formatCurrency(order.subtotal || 0)}</span>
                </div>
                {order.shipping !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Kargo</span>
                    <span className="font-medium">
                      {order.shipping === 0 ? 'Ücretsiz' : formatCurrency(order.shipping)}
                    </span>
                  </div>
                )}
                {order.tax !== undefined && order.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">KDV</span>
                    <span className="font-medium">{formatCurrency(order.tax)}</span>
                  </div>
                )}
                {order.discount !== undefined && order.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>İndirim</span>
                    <span>-{formatCurrency(order.discount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Toplam</span>
                  <span>{formatCurrency(order.total || 0)}</span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            {order.paymentMethod === 'bank_transfer' && order.status === 'pending' && (
              <div className="bg-white rounded-lg border p-6">
                <h2 className="text-xl font-semibold mb-4">Ödeme Bilgileri</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Müşteri aşağıdaki banka hesabına havale/EFT yapabilir:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Banka Hesap Bilgileri</p>
                  <p className="text-sm text-gray-600">
                    (Banka bilgileri admin ayarlarından yönetilebilir)
                  </p>
                  <p className="text-xs text-gray-500 mt-4">
                    Sipariş No: <span className="font-mono font-medium">{order.orderNumber}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Tutar: <span className="font-medium">{formatCurrency(order.total || 0)}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none;
          }
          .print\\:p-0 {
            padding: 0;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default OrderDetailPage;
